import React, { useState } from "react";
import { Loader2, Sparkles, X, Wand2, Download, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Field-specific example hints so the "Give the AI a hint" copy reads
// relevant to whatever the user is actually editing.
const HINT_EXAMPLES = {
    // Creator profile
    name_title: 'e.g. "Positioning Consultant" or "Founder & Brand Strategist".',
    communication_style: 'e.g. "Bold and direct, no fluff" or "Warm, storytelling, contrarian".',
    bio_story: 'e.g. "Left corporate marketing in 2018 after burnout" or "Started in publishing, pivoted to coaching".',
    creator_bio: 'e.g. "I help credentialed coaches become the obvious choice through positioning".',
    credibility_wins: 'e.g. "Featured in Forbes, 200+ clients, signature framework called Authority Positioning System".',
    credibility: 'e.g. "10 years in the industry, 200+ clients, Forbes feature".',
    signature_phrase: 'e.g. "The market does not want the most experienced. It wants the most obvious."',
    brand_words: 'e.g. "Obvious Choice, Positioning Gap, Un-Cloned, Authority Layer, 5 Must-Haves".',
    contrarian_belief: 'e.g. "Being qualified is the #1 reason experts stay invisible online".',
    industry_gets_wrong: 'e.g. "The industry teaches credentials over positioning — that\'s why so many pros stay invisible".',
    why_message_works_now: 'e.g. "AI has flattened content. Positioning is now the last competitive edge".',
    why_brand_exists: 'e.g. "Because too many qualified experts stay invisible while newcomers get the clients".',
    default_cta: 'e.g. "DM the word COACH to book a call" or "Apply at mylink.com".',
    desired_cta: 'e.g. "DM the word COACH" or "Apply for the intensive at mylink.com".',
    offer_and_price: 'e.g. "1:1 positioning consulting, $5,000 for 90 days".',
    // Avatar / audience
    avatar_type: 'e.g. "This is a target situation, not a demographic" or "Both — a specific persona in a specific moment".',
    who_for: 'e.g. "Female coaches 5+ years in, still invisible on social despite their results".',
    who_not_for: 'e.g. "Brand-new coaches without any clients yet" or "Anyone looking for quick tactics without doing brand work".',
    who_for_who_not: 'e.g. "For established coaches with results. Not for beginners looking for tactics".',
    ideal_buyer: 'e.g. "Coaches 5+ years in who have credentials but no visibility. They want to be seen as the go-to".',
    their_pain: 'e.g. "Watching newer, less experienced people get the clients while feeling completely invisible".',
    desired_outcome: 'e.g. "Becoming the obvious go-to in their niche without changing who they are".',
    tried_before: 'e.g. "Posted daily for a year, hired a content manager, got more certifications — nothing moved".',
    objections: 'e.g. "They think they just need more credentials or to post more consistently".',
    buyer_objections: 'e.g. "They believe positioning is fluffy branding — they want tactics instead".',
    their_language: 'e.g. "Phrases like \'I\'m highly qualified\' or \'nobody sees me\' or \'what am I doing wrong?\'".',
    differentiator: 'e.g. "We focus on positioning, not tactics. Every client leaves with a framework, not a checklist".',
    // Per-video brief
    video_topic: 'e.g. "Why credentials are the reason coaches stay invisible" or "The obvious-choice framework".',
    video_pov: 'e.g. "Certifications signal safety, not authority" or "Being qualified is table stakes, not a differentiator".',
    video_myth: 'e.g. "The advice that says get one more cert and clients will finally take you seriously".',
    video_data: 'e.g. "The coach industry grew 500% in 10 years. 90% still can\'t fill their first offer".',
    video_story: 'e.g. "A client with 4 certifications and 7 years experience — first month after repositioning: 3 calls".',
    video_belief_shift: 'e.g. "Certifications are the floor, not the ceiling. Positioning is what makes you unforgettable".',
};

const defaultHint = 'Optional — a short note to steer the draft. Leave blank and I\'ll draft based on what you\'ve filled in elsewhere.';

/**
 * "Help Me Write This" button + dialog. Calls POST /me/profile-assist
 * with the current field, an optional hint from the user, and the rest of the
 * profile so the AI can stay consistent. Returns 3 draft options; clicking one
 * inserts it into the field via the onApply callback.
 */
export const AiAssistButton = ({ fieldKey, fieldLabel, currentFields, currentValue, onApply, projectId, className = "" }) => {
    const [open, setOpen] = useState(false);
    const [hint, setHint] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [options, setOptions] = useState([]);
    const [sources, setSources] = useState([]);

    const generate = async () => {
        setLoading(true);
        setOptions([]);
        setSources([]);
        try {
            const { data } = await api.post("/me/profile-assist", {
                field_key: fieldKey,
                field_label: fieldLabel,
                hint,
                current_fields: currentFields,
            });
            setOptions(data.options || []);
            setSources(data.sources || []);
            if (!data.options || data.options.length === 0) {
                toast.error("No options came back — try adding a hint");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI assistant failed");
        } finally {
            setLoading(false);
        }
    };

    const apply = (text, mode = "replace") => {
        // Just insert the draft text as-is. Sources are kept in the project's
        // Research Library, NOT injected into the wizard input, so they don't
        // leak into the final script text.
        if (mode === "append" && currentValue?.trim()) {
            onApply((currentValue + " " + text).trim());
        } else {
            onApply(text);
        }
        toast.success("Draft applied");
        setOpen(false);
        setOptions([]);
        setSources([]);
        setHint("");
    };

    const saveResearchToProject = async () => {
        if (!projectId) {
            toast.error("No project attached to save research to");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/projects/${projectId}/research`, {
                field_key: fieldKey,
                field_label: fieldLabel || fieldKey,
                topic: (currentFields?.video_topic || currentFields?.topic || "").trim(),
                hint,
                options,
                sources,
            });
            toast.success("Research saved to project");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const downloadResearch = () => {
        const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const topic = (currentFields?.video_topic || currentFields?.topic || "").trim();
        const headerLines = [
            `# UnCloned® Research — ${fieldLabel || fieldKey}`,
            "",
            `**Generated:** ${new Date().toLocaleString()}`,
        ];
        if (topic) headerLines.push(`**Video topic:** ${topic}`);
        if (hint) headerLines.push(`**Hint used:** ${hint}`);
        headerLines.push("", "---", "");

        const draftLines = ["## Drafts", ""];
        options.forEach((o, i) => {
            draftLines.push(`### Option ${i + 1}`, "", o, "");
        });
        draftLines.push("---", "");

        const sourceLines = ["## Sources", ""];
        sources.forEach((s, i) => {
            sourceLines.push(`### [${i + 1}] ${s.title || "Untitled"}`);
            sourceLines.push(`**URL:** ${s.url}`);
            sourceLines.push("");
            if (s.snippet) {
                sourceLines.push(`> ${s.snippet.replace(/\n/g, "\n> ")}`);
                sourceLines.push("");
            }
        });
        sourceLines.push("---", "");
        sourceLines.push("_Sources gathered via Tavily web search. Always verify each statistic on the source page before use._", "");

        const md = [...headerLines, ...draftLines, ...sourceLines].join("\n");

        const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `uncloned-research-${fieldKey}-${stamp}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Research downloaded");
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={`inline-flex items-center gap-2 border-2 border-un-onyx px-3 py-2 text-xs font-bold uppercase tracking-wider bg-un-lime text-un-onyx hover:bg-un-onyx hover:text-un-lime transition-colors ${className}`}
                data-testid={`ai-help-btn-${fieldKey}`}
                title="Have the AI draft this for you"
            >
                <Wand2 className="w-4 h-4" />
                Help Me Write This
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-un-onyx/70"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setOpen(false)}
                    data-testid={`ai-help-dialog-${fieldKey}`}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="un-card max-w-2xl w-full bg-white max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between border-b-2 border-un-onyx px-6 py-4 sticky top-0 bg-white z-10">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-un-berry" />
                                <div>
                                    <div className="un-label text-un-grape">AI Assistant</div>
                                    <div className="text-sm font-bold text-un-onyx mt-0.5">{fieldLabel}</div>
                                </div>
                            </div>
                            <button onClick={() => setOpen(false)} className="un-btn-ghost !px-1 !py-1" aria-label="Close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {fieldKey === "video_data" && (
                                <div
                                    className="border-2 border-un-onyx bg-un-water/40 p-3 text-sm text-un-onyx"
                                    data-testid="video-data-web-notice"
                                >
                                    <span className="un-mono text-[0.65rem] font-bold text-un-berry block mb-1">LIVE WEB SEARCH</span>
                                    I&apos;ll search the web for real statistics, benchmarks, and studies tied to your video topic, then draft 3 stat-focused options with numbered citations <span className="un-mono font-bold">[1] [2] [3]</span>.
                                    Sources will appear underneath — always click through to confirm the number before you use it on camera.
                                </div>
                            )}
                            <div>
                                <div className="text-base font-bold text-un-onyx block mb-2">
                                    Give the AI a hint for <span className="text-un-berry">{fieldLabel || fieldKey}</span>
                                </div>
                                <p className="text-un-onyx/70 text-sm mb-3 leading-relaxed">
                                    {HINT_EXAMPLES[fieldKey] || defaultHint}
                                    {HINT_EXAMPLES[fieldKey] ? " Leave blank and I'll draft based on your profile." : ""}
                                </p>
                                <textarea
                                    rows={3}
                                    value={hint}
                                    onChange={(e) => setHint(e.target.value)}
                                    placeholder={HINT_EXAMPLES[fieldKey]?.replace(/^e\.g\. /, "").replace(/\.$/, "") || "Optional context, ideas, or direction…"}
                                    className="un-input text-base"
                                    data-testid={`ai-help-hint-${fieldKey}`}
                                />
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={generate}
                                    disabled={loading}
                                    className="un-btn-primary text-sm inline-flex items-center gap-2"
                                    data-testid={`ai-help-generate-${fieldKey}`}
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    {loading
                                        ? (fieldKey === "video_data" ? "Searching web…" : "Drafting…")
                                        : (fieldKey === "video_data" ? "Search Web & Draft 3 Options" : "Draft 3 Options")}
                                </button>
                            </div>

                            {options.length > 0 && (
                                <div className="pt-4 un-divider">
                                    <div className="un-label text-un-grape mb-3 text-sm">3 Drafts · Click To Insert</div>
                                    <div className="space-y-3">
                                        {options.map((o, i) => (
                                            <div
                                                key={i}
                                                className="border-2 border-un-onyx bg-white"
                                                data-testid={`ai-help-option-${fieldKey}-${i}`}
                                            >
                                                <div className="px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap text-un-onyx">
                                                    {o}
                                                </div>
                                                <div className="border-t-2 border-un-onyx flex divide-x-2 divide-un-onyx">
                                                    <button
                                                        onClick={() => apply(o, "replace")}
                                                        className="flex-1 py-2 text-xs font-bold uppercase tracking-wider hover:bg-un-berry hover:text-white transition-colors"
                                                        data-testid={`ai-help-use-${fieldKey}-${i}`}
                                                    >
                                                        Use This
                                                    </button>
                                                    {(currentValue || "").trim() && (
                                                        <button
                                                            onClick={() => apply(o, "append")}
                                                            className="flex-1 py-2 text-xs font-bold uppercase tracking-wider hover:bg-un-onyx hover:text-white transition-colors"
                                                            data-testid={`ai-help-append-${fieldKey}-${i}`}
                                                        >
                                                            Append To Current
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {sources.length > 0 && (
                                <div className="pt-4 un-divider" data-testid={`ai-help-sources-${fieldKey}`}>
                                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                                        <div className="un-label text-un-grape text-sm">Sources · Verify Each Number Before Use</div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {projectId && (
                                                <button
                                                    type="button"
                                                    onClick={saveResearchToProject}
                                                    disabled={saving}
                                                    className="un-btn-primary text-xs inline-flex items-center gap-2"
                                                    data-testid={`ai-help-save-research-${fieldKey}`}
                                                    title="Save this research session to the project's Research Library"
                                                >
                                                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                                    {saving ? "Saving…" : "Save To Project"}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={downloadResearch}
                                                className="un-btn-outline text-xs inline-flex items-center gap-2"
                                                data-testid={`ai-help-download-research-${fieldKey}`}
                                                title="Download all drafts + sources as a markdown file"
                                            >
                                                <Download className="w-3 h-3" />
                                                Download .md
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {sources.map((s, i) => (
                                            <a
                                                key={s.url || i}
                                                href={s.url}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="block border-2 border-un-onyx p-3 bg-white hover:bg-un-lime/40 transition-colors"
                                                data-testid={`ai-help-source-${i + 1}`}
                                            >
                                                <div className="flex items-baseline gap-2 mb-1">
                                                    <span className="un-mono text-[0.65rem] font-bold text-un-berry">[{i + 1}]</span>
                                                    <span className="text-sm font-bold text-un-onyx break-words">
                                                        {s.title || s.url}
                                                    </span>
                                                </div>
                                                <div className="un-mono text-[0.65rem] text-un-onyx/60 break-all mb-1">{s.url}</div>
                                                {s.snippet && (
                                                    <div className="text-xs text-un-onyx/70 line-clamp-3 leading-relaxed">{s.snippet}</div>
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {fieldKey === "video_data" && options.length > 0 && sources.length === 0 && (
                                <div className="pt-2 text-xs text-un-onyx/70" data-testid="no-live-sources-note">
                                    No live sources returned — these drafts are directional only. Paste your own stat if you have one.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
